import {
    type AnyRoute,
    type AnyRouter,
    type RegisteredRouter,
    rootRouteId,
    useMatches,
    useRouter,
} from '@tanstack/react-router';

import { movedPagesOf } from './movedPages';

/** The root splat: it claims every URL no other route does, and throws `notFound()`. */
const CATCH_ALL = '/$';

/** A link's `to` in the registered route tree: every route's full path, an index route's without its `/`. */
type RouteTo = RegisteredRouter['routeTree']['types']['fileRouteTypes']['to'];

/**
 * A route's path as a link's `to` spells it (`/projects/$projectId/dashboard`, `/projects`, `/orders/{-$orderId}`),
 * which Amplitude page events (`page: <name>`) and the Sentry `route` tag carry.
 * Typed by the app's route tree, so comparing `useRouteName()` with a name no route has fails tsc: type the names a
 * caller compares it with as this. A plain string where no router is registered (the design system's own build).
 */
export type RouteName = Exclude<string extends RouteTo ? string : RouteTo, typeof CATCH_ALL>;

/**
 * The {@link RouteName} of the last of `matches`, the route a URL lands on: the one naming rule `useRouteName`, page
 * views and the Sentry tag share. `undefined` when that route is no page of its own:
 * - the root, or a pathless layout (`_authenticated`), which is the last match only when no route below it matches;
 * - the root splat. Other splats are named by their path, such as `/apps/$`.
 *
 * Any other route that throws `notFound()` from its `beforeLoad` or `loader` keeps its name: the URL names that
 * route, and the Sentry tag is set before the load, which can't know how it ends.
 */
export function routeNameOf(router: AnyRouter, matches: ReadonlyArray<{ routeId: string }>): RouteName | undefined {
    const leaf = matches.at(-1);
    if (leaf === undefined || leaf.routeId === rootRouteId) {
        return undefined;
    }
    const route: AnyRoute = router.routesById[leaf.routeId];
    // A pathless layout has an `id` instead of a `path`, and shares its parent's `to`.
    if (route.path === undefined || route.to === CATCH_ALL) {
        return undefined;
    }
    return route.to as RouteName;
}

/**
 * The name of the route on screen: it moves in the same render as the page, not when a navigation away starts. A page
 * with a second route (`createAppRouter`'s `movedPages`) has the name of the route links point at under both.
 */
export function useRouteName(): RouteName | undefined {
    const router = useRouter();
    return useMatches({
        select: (matches) => {
            const name = routeNameOf(router, matches);
            return (movedPagesOf(router).find(({ to }) => to === name)?.from ?? name) as RouteName | undefined;
        },
    });
}

/**
 * Calls `listener` with the {@link routeNameOf} of the route each load goes to, for the Sentry `route` tag, and
 * returns the unsubscribe function. On `router-kit`, not `index.ts`: its signature names a TanStack type.
 */
export function subscribeToRouteTag(
    router: AnyRouter,
    listener: (routeName: RouteName | undefined) => void,
): () => void {
    // The start URL right away, so an error thrown before the first load (in a provider above the router) is tagged.
    listener(routeNameOf(router, router.matchRoutes(router.latestLocation)));
    // Fires as each load starts, the first included, before any `beforeLoad`: an error while a page loads or renders
    // carries its name, and a URL that only redirects is named before the page it redirects to.
    return router.subscribe('onBeforeNavigate', ({ toLocation }) => {
        listener(routeNameOf(router, router.matchRoutes(toLocation)));
    });
}
